/**
 * Shared logical routes. Every frontend links via a `routeKey` + params so PL and EN
 * paths stay in lock-step across versions. `:param` placeholders are filled from
 * `params` and URL-encoded.
 */
import type { Locale } from "@mb-group/shared-types";

interface RouteDefinition {
	pl: string;
	en: string;
}

const ROUTES = {
	home: { pl: "/", en: "/" },
	services: { pl: "/uslugi", en: "/services" },
	serviceDetail: { pl: "/uslugi/:slug", en: "/services/:slug" },
	companies: { pl: "/spolki", en: "/companies" },
	companyDetail: { pl: "/spolki/:slug", en: "/companies/:slug" },
	caseStudies: { pl: "/realizacje", en: "/case-studies" },
	caseStudyDetail: { pl: "/realizacje/:slug", en: "/case-studies/:slug" },
	knowledge: { pl: "/baza-wiedzy", en: "/knowledge" },
	article: { pl: "/baza-wiedzy/:slug", en: "/knowledge/:slug" },
	resources: { pl: "/raporty", en: "/resources" },
	webinars: { pl: "/webinary", en: "/webinars" },
	landing: { pl: "/lp/:slug", en: "/lp/:slug" },
	search: { pl: "/szukaj", en: "/search" },
	contact: { pl: "/kontakt", en: "/contact" },
	privacy: { pl: "/prywatnosc", en: "/privacy" },
	cookies: { pl: "/cookies", en: "/cookies" },
} as const satisfies Record<string, RouteDefinition>;

/** Union of all known logical route keys. */
export type RouteKey = keyof typeof ROUTES;

/** All route keys as a runtime array (e.g. for validation or iteration). */
export const ROUTE_KEYS: string[] = Object.keys(ROUTES);

/** Type guard: is `value` a defined {@link RouteKey}? */
export function isRouteKey(value: string): value is RouteKey {
	return value in ROUTES;
}

/** Locale and path params for {@link resolveRoute}. */
export interface ResolveRouteOptions {
	locale: Locale;
	params?: Record<string, string | number>;
}

/**
 * Resolve a logical route key to a locale-specific path, filling and URL-encoding
 * `:param` placeholders.
 *
 * @param routeKey A key from {@link ROUTE_KEYS}.
 * @param options Target locale and any required path params.
 * @returns The resolved path (e.g. `/uslugi/recykling-baterii`).
 * @throws Error if the route key is unknown or a required param is missing.
 */
export function resolveRoute(routeKey: string, options: ResolveRouteOptions): string {
	if (!isRouteKey(routeKey)) {
		throw new Error(`Unknown route key: ${routeKey}`);
	}
	const template = ROUTES[routeKey][options.locale];
	const params = options.params ?? {};

	return template.replace(/:([A-Za-z0-9_]+)/g, (_match, name: string) => {
		const value = params[name];
		if (value === undefined) {
			throw new Error(`Missing route param "${name}" for route "${routeKey}"`);
		}
		return encodeURIComponent(String(value));
	});
}
